import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trash as TrashIcon, ArrowCounterClockwise, Warning, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Trash = ({ user }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [restoring, setRestoring] = useState(null);

  const isSuperAdmin = user?.is_super_admin || user?.role?.toLowerCase() === 'admin';

  const fetchDeletedDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/documents`, {
        params: { deleted_only: true }
      });
      setDocuments(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch deleted documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeletedDocuments();
  }, [fetchDeletedDocuments]);

  const handleRestore = async (doc) => {
    try {
      setRestoring(doc.id);
      await axios.post(`${API}/documents/${doc.id}/restore`);
      toast.success(`Restored "${doc.document_number || doc.name}"`);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (error) {
      toast.error('Failed to restore document');
    } finally {
      setRestoring(null);
    }
  };

  const handlePermanentDelete = async (doc) => {
    try {
      await axios.delete(`${API}/documents/${doc.id}/permanent`);
      toast.success(`Permanently deleted "${doc.document_number || doc.name}"`);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      setConfirmDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(String(dateString).endsWith('Z') || String(dateString).includes('+') ? dateString : dateString + 'Z');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filtered = documents.filter(doc => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (doc.document_number || '').toLowerCase().includes(term) ||
           (doc.name || '').toLowerCase().includes(term) ||
           (doc.title || '').toLowerCase().includes(term);
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="trash-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900" data-testid="trash-title">Deleted Documents</h1>
          <p className="text-sm text-gray-500 mt-1">{documents.length} document{documents.length !== 1 ? 's' : ''} in trash</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search deleted documents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          data-testid="trash-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400" data-testid="trash-empty">
          <TrashIcon size={48} weight="thin" />
          <p className="mt-3 text-sm">{searchTerm ? 'No matching deleted documents' : 'Trash is empty'}</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="trash-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-medium text-gray-600">Document Number</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Title</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Discipline</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Deleted At</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((doc) => (
                <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50/50" data-testid={`trash-row-${doc.id}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-gray-900 font-medium">{doc.document_number || '-'}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 max-w-xs truncate">{doc.title || doc.name || '-'}</td>
                  <td className="px-4 py-3 text-gray-500">{doc.discipline || '-'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(doc.deleted_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs border-green-200 text-green-700 hover:bg-green-50"
                        onClick={() => handleRestore(doc)}
                        disabled={restoring === doc.id}
                        data-testid={`restore-btn-${doc.id}`}
                      >
                        <ArrowCounterClockwise size={14} className="mr-1" />
                        {restoring === doc.id ? 'Restoring...' : 'Restore'}
                      </Button>
                      {isSuperAdmin && (
                        <>
                          {confirmDelete === doc.id ? (
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="destructive"
                                className="h-8 text-xs"
                                onClick={() => handlePermanentDelete(doc)}
                                data-testid={`confirm-delete-btn-${doc.id}`}
                              >
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 text-xs"
                                onClick={() => setConfirmDelete(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setConfirmDelete(doc.id)}
                              data-testid={`permanent-delete-btn-${doc.id}`}
                            >
                              <TrashIcon size={14} className="mr-1" />
                              Delete Forever
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Warning */}
      {isSuperAdmin && documents.length > 0 && (
        <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
          <Warning size={16} className="shrink-0 mt-0.5" />
          <span>Permanently deleted documents cannot be recovered. Use with caution.</span>
        </div>
      )}
    </div>
  );
};

export default Trash;
